import { cn } from "../../lib/utils";
import { Card } from "../../components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

export interface HeroKPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  trendLabel?: string;
  icon: React.ReactNode;
  variant?: "default" | "success" | "warning" | "danger" | "info";
  onClick?: () => void;
}

const variantStyles = {
  default: {
    ring: "ring-primary/20",
    gradient: "from-primary/5 to-transparent",
    icon: "bg-primary/10 text-primary",
  },
  success: {
    ring: "ring-green-500/20",
    gradient: "from-green-50/60 to-transparent",
    icon: "bg-green-100 text-green-600",
  },
  warning: {
    ring: "ring-amber-500/20",
    gradient: "from-amber-50/60 to-transparent",
    icon: "bg-amber-100 text-amber-600",
  },
  danger: {
    ring: "ring-red-500/20",
    gradient: "from-red-50/60 to-transparent",
    icon: "bg-red-100 text-red-600",
  },
  info: {
    ring: "ring-blue-500/20",
    gradient: "from-blue-50/60 to-transparent",
    icon: "bg-blue-100 text-blue-600",
  },
};

export const HeroKPICard = ({
  title,
  value,
  subtitle,
  trend,
  trendLabel = "vs last month",
  icon,
  variant = "default",
  onClick,
}: HeroKPICardProps) => {
  const styles = variantStyles[variant];

  const getTrendIcon = () => {
    if (trend === undefined || trend === 0) return <Minus className="h-3 w-3" />;
    return trend > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />;
  };

  const getTrendColor = () => {
    if (trend === undefined || trend === 0) return "text-muted-foreground";
    if (variant === "danger" || variant === "warning") {
      return trend > 0 ? "text-red-600" : "text-green-600";
    }
    return trend > 0 ? "text-green-600" : "text-red-600";
  };

  return (
    <Card
      className={cn(
        "relative overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-200 group",
        "ring-1 ring-inset",
        "bg-gradient-to-br",
        styles.ring,
        styles.gradient,
        onClick && "hover:scale-[1.01]"
      )}
      onClick={onClick}
    >
      <div className="p-4 h-[110px] flex flex-col justify-between">
        {/* Header */}
        <div className="flex items-start justify-between">
          <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground leading-tight">
            {title}
          </span>
          <div
            className={cn(
              "h-8 w-8 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110 shrink-0",
              styles.icon
            )}
          >
            {icon}
          </div>
        </div>

        {/* Value */}
        <div className="space-y-1">
          <div className="text-2xl font-bold tracking-tight">{value}</div>
          
          <div className="flex items-center justify-between">
            {subtitle && (
              <span className="text-[10px] text-muted-foreground truncate mr-2">{subtitle}</span>
            )}
            
            {trend !== undefined && (
              <div className={cn("flex items-center gap-1 text-[10px] shrink-0", getTrendColor())}>
                {getTrendIcon()}
                <span>
                  {trend > 0 ? "+" : ""}
                  {trend}% {trendLabel}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default HeroKPICard;
